import React from "react";
import { ChevronDown, Eye, Loader2, Play, Pause, ShieldCheck, AlertCircle } from "lucide-react";
import { formatDateTimeET } from "../../utils/datetime";
import StrategySymbolChart from "./StrategySymbolChart";
import Modal from "../Modal";
import StrategyConfigPanel from "../StrategyConfigPanel";
import {
    getAdaptiveLevelTone,
    hasFreshLevels,
    isCurrentNySessionDate,
    isEarlyRegularSession,
    isRegularMarketOpen,
    isValidPriceLevel,
} from "./strategyStatusUtils";
import LiveStrategyPrice from "./LiveStrategyPrice";
import { useQuote } from "../../market/QuoteStore";

interface ORBStrategyContentProps {
    orbStatusState: any;
    handleStrategyToggle: (strategyName: "ON" | "ORB" | "DT", currentlyEnabled: boolean | undefined) => Promise<void>;
    strategyToggleLoading: Partial<Record<"ON" | "ORB" | "DT", "starting" | "pausing">>;
}

interface ORBSymbolRowProps {
    symbol: string;
    status: any;
    levelsDateIso?: string;
    marketOpen: boolean;
    expanded: boolean;
    onToggle: () => void;
}

const ORBSymbolRow = React.memo<ORBSymbolRowProps>(({ symbol, status, levelsDateIso, marketOpen, expanded, onToggle }) => {
    const quoteIdentifier = status?.evaluated_quote?.local_symbol || symbol;
    const streamedQuote = useQuote(quoteIdentifier);
    const livePrice = Number(streamedQuote?.price ?? status?.live_quote?.price ?? status?.current_price);
    const decisionPrice = Number(status.decision_price);
    const signalPrice = Number.isFinite(decisionPrice) && decisionPrice > 0
        ? decisionPrice
        : Number(status.current_price);
    const resistance = Number(status.resistance);
    const support = Number(status.support);
    const hasCurrentLevels = hasFreshLevels(status, levelsDateIso) &&
        isCurrentNySessionDate(status, levelsDateIso) &&
        isValidPriceLevel(resistance) &&
        isValidPriceLevel(support);
    const levelTone = getAdaptiveLevelTone({
        price: isValidPriceLevel(livePrice) ? livePrice : signalPrice,
        lowerLevel: support,
        upperLevel: resistance,
        levelsReady: hasCurrentLevels,
        earlySession: isEarlyRegularSession(),
        marketOpen,
    });

    return (
        <>
            <tr
                className="cursor-pointer transition-colors hover:bg-white/5"
                style={levelTone.rowStyle}
                title={levelTone.label}
                onClick={onToggle}
            >
                <td className="px-4 py-3 font-bold text-[14px] text-white">
                    <div className="flex items-center gap-2">
                        <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform ${expanded ? "rotate-180 text-neon" : ""}`} />
                        <div>
                            <span>{symbol}</span>
                            <span className={`block text-[10px] font-medium ${levelTone.priceClass}`}>{levelTone.label}</span>
                            {status.execution_sec_type === "FUT" && status.execution_display_symbol && (
                                <span className="block text-[10px] font-mono font-normal text-amber-300/80">
                                    → {status.execution_display_symbol}
                                </span>
                            )}
                        </div>
                    </div>
                </td>
                <td className={`px-4 py-3 text-right font-mono ${levelTone.priceClass}`}>
                    <LiveStrategyPrice
                        symbol={symbol}
                        evaluatedPrice={status.current_price}
                        evaluatedQuote={status.evaluated_quote}
                        initialLiveQuote={status.live_quote}
                        decisionPrice={status.decision_price}
                        decisionSource={status.decision_price_source}
                        decisionLabel="5m decision"
                        streamedQuoteOverride={streamedQuote}
                        priceClassName={levelTone.priceClass}
                    />
                </td>
                <td className="px-4 py-3 text-right font-mono">
                    {status.daily_open ? `$${Number(status.daily_open).toFixed(2)}` : "—"}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                    {status.atr_value ? `$${Number(status.atr_value).toFixed(2)}` : "—"}
                </td>
                <td className={`px-4 py-3 text-right font-mono ${levelTone.lowerClass}`}>
                    {status.support ? `$${Number(status.support).toFixed(2)}` : "—"}
                </td>
                <td className={`px-4 py-3 text-right font-mono ${levelTone.upperClass}`}>
                    {status.resistance ? `$${Number(status.resistance).toFixed(2)}` : "—"}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                    {status.current_vwap ? `$${Number(status.current_vwap).toFixed(2)}` : "—"}
                </td>
                <td className="px-4 py-3 text-right font-mono text-white/80">
                    {status.calculated_size ? status.calculated_size : "—"}
                    {status.execution_sec_type === 'FUT' && status.calculated_size !== undefined && status.calculated_size !== null && status.calculated_size !== 0 && (
                        <span className="ml-1 text-[10px] text-white/40">ct</span>
                    )}
                </td>
                <td className="px-4 py-3 text-right font-mono text-white/80">
                    {status.current_leverage ? `${status.current_leverage}x` : "—"}
                </td>
                <td className="px-4 py-3 text-right font-medium">
                    {status.position ? (
                        typeof status.position === 'number' ? (
                            <>
                                {status.position}
                                {status.execution_sec_type === 'FUT' && status.position !== 0 && (
                                    <span className="ml-1 text-[10px] text-white/40">ct</span>
                                )}
                            </>
                        ) : (
                            <>
                                {status.position.shares || 0}
                                {status.execution_sec_type === 'FUT' && (status.position.shares || 0) !== 0 && (
                                    <span className="ml-1 text-[10px] text-white/40">ct</span>
                                )}
                            </>
                        )
                    ) : "—"}
                </td>
            </tr>
            {expanded && (
                <tr>
                    <td colSpan={10} className="p-0 bg-black/20">
                        <StrategySymbolChart symbol={symbol} timeframe="5min" label="ORB" strategy="ORB" status={{ ...status, levels_date: status.levels_date || levelsDateIso }} />
                    </td>
                </tr>
            )}
        </>
    );
});

const ORBStrategyContent: React.FC<ORBStrategyContentProps> = ({
    orbStatusState,
    handleStrategyToggle,
    strategyToggleLoading,
}) => {
    const [showRules, setShowRules] = React.useState(false);
    const [expandedSymbol, setExpandedSymbol] = React.useState<string | null>(null);
    const [showConfigModal, setShowConfigModal] = React.useState(false);
    const [marketOpen, setMarketOpen] = React.useState(() => isRegularMarketOpen());

    React.useEffect(() => {
        const updateMarketState = () => setMarketOpen(isRegularMarketOpen());
        const interval = window.setInterval(updateMarketState, 30_000);
        return () => window.clearInterval(interval);
    }, []);

    if (!orbStatusState) return null;
    const orbData = orbStatusState.data || ({} as any);
    const orbEnabled = orbData.enabled ?? false;
    const toggleState = strategyToggleLoading.ORB;

    const levelsDateIso = orbData?.levels_date as string | undefined;
    const levelsDate =
        levelsDateIso && !Number.isNaN(Date.parse(levelsDateIso))
            ? formatDateTimeET(levelsDateIso)
            : undefined;

    return (
        <>
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted">Strategy</span>
                    <span className="font-semibold text-foreground">ORB</span>
                    {orbData?.version && (
                        <span className="px-1.5 py-0.5 rounded-md bg-white/5 border border-white/10 text-[10px] text-muted font-mono" title="Strategy Logic Version">
                            v{orbData.version}
                        </span>
                    )}
                </div>
                <button
                    onClick={() => handleStrategyToggle("ORB", orbEnabled)}
                    disabled={!!toggleState}
                    aria-busy={!!toggleState}
                    className={`min-w-[104px] justify-center px-3 py-1.5 rounded-lg font-semibold uppercase flex text-sm items-center gap-1 transition disabled:cursor-wait disabled:opacity-80 ${toggleState ? "bg-amber-400/10 text-amber-300 border border-amber-400/20" : orbEnabled ? "bg-neon-soft text-neon" : "text-muted hover:text-foreground hover:bg-white/10"
                        }`}
                >
                    {toggleState ? <Loader2 size={14} className="animate-spin" /> : orbEnabled ? <Pause size={14} /> : <Play size={14} />}
                    {toggleState === "starting" ? "Starting" : toggleState === "pausing" ? "Pausing" : orbEnabled ? "Pause" : "Start"}
                </button>
            </div>

            <div className="text-foreground">
                <button
                    onClick={() => setShowRules(!showRules)}
                    className="flex items-center gap-2 mb-4 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-[11px] font-black uppercase tracking-[0.2em] text-muted hover:text-white hover:bg-white/10 transition-all shadow-inner group"
                >
                    <div className={`p-1 rounded-md transition-all ${showRules ? 'bg-neon/20 text-neon' : 'bg-white/10 text-white/40 group-hover:text-white'}`}>
                        <ShieldCheck size={14} className={showRules ? 'animate-pulse' : ''} />
                    </div>
                    <span>{showRules ? 'Hide' : 'Show'} Strategy Conditions & Rules</span>
                    <div className={`ml-auto transition-transform duration-300 ${showRules ? 'rotate-180 text-neon' : ''}`}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
                    </div>
                </button>

                {showRules && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3 shadow-lg">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="p-1.5 rounded-lg bg-neon/10 text-neon">
                                    <ShieldCheck size={16} />
                                </div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-white/90">Entry Conditions</h4>
                            </div>
                            <ul className="space-y-2">
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-neon/50 shrink-0" />
                                    <span>Price breakout of <b className="text-white/80">Opening Range</b> (Open ± ATR factor)</span>
                                </li>
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-neon/50 shrink-0" />
                                    <span>Entry window: <b className="text-white/80">09:35 - 09:35:30 ET</b></span>
                                </li>
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-neon/50 shrink-0" />
                                    <span>Confirmed by <b className="text-white/80">5-minute bar</b> close</span>
                                </li>
                            </ul>
                        </div>

                        <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-3 shadow-lg">
                            <div className="flex items-center gap-2 mb-1">
                                <div className="p-1.5 rounded-lg bg-amber-500/10 text-amber-400">
                                    <AlertCircle size={16} />
                                </div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-white/90">Strategy Rules</h4>
                            </div>
                            <ul className="space-y-2">
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-amber-400/50 shrink-0" />
                                    <span>Positions liquidated at <b className="text-white/80">15:30 ET</b> (EOD)</span>
                                </li>
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-amber-400/50 shrink-0" />
                                    <span>ATR-based <b className="text-white/80">Stop Loss</b> set at entry</span>
                                </li>
                                <li className="flex items-start gap-2 text-[11px] text-muted leading-relaxed">
                                    <span className="mt-1 w-1 h-1 rounded-full bg-amber-400/50 shrink-0" />
                                    <span>Optional <b className="text-white/80">VWAP Exit</b> if enabled</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-foreground">
                {levelsDate && (
                    <div className="rounded-xl bg-white/6 border border-white/10 px-3 py-2 flex items-center justify-between">
                        <span className="text-muted mr-2">Latest Data</span>
                        <span className="text-foreground font-semibold">{levelsDate}</span>
                    </div>
                )}
            </div>

            <div className="mt-4 rounded-2xl bg-white/6 text-sm text-foreground">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-neon">Symbol Status</h3>
                    {orbData.config_source && (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setShowConfigModal(true)}
                                className={`inline-flex min-h-7 shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider transition-colors ${orbData.config_source === 'USER'
                                ? 'bg-neon/10 text-neon border border-neon/20'
                                : 'bg-white/5 text-muted border border-white/10'
                                } hover:bg-white/10 hover:text-white`}
                                title="View current ORB strategy setup"
                            >
                                <Eye className="h-3.5 w-3.5" />
                                <span className="whitespace-nowrap">{orbData.config_source === 'USER' ? 'User Config' : 'System Default'}</span>
                                {orbData.config_version && <span className="whitespace-nowrap">v{orbData.config_version}</span>}
                            </button>
                        </div>
                    )}
                </div>

                <div className="overflow-x-auto scrollbar-thin rounded-xl border border-white/5 bg-surface">
                    <table className="min-w-full divide-y divide-white/10 text-[13px] text-foreground">
                        <thead className="bg-black/60">
                            <tr>
                                <th scope="col" className="px-4 py-4 text-left font-bold text-white/90 uppercase tracking-wider">Symbol</th>
                                <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Price</th>
                                <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Open</th>
                                <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">ATR</th>
                                <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">ORB Low</th>
                                <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">ORB High</th>
                                <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">VWAP</th>
                                <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Size</th>
                                <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Lev</th>
                                <th scope="col" className="px-4 py-4 text-right font-bold text-white/90 uppercase tracking-wider">Position</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {orbData.symbol_status && Object.keys(orbData.symbol_status).length > 0 ? (
                                Object.entries(orbData.symbol_status).map(([symbol, status]: [string, any]) => (
                                    <ORBSymbolRow
                                        key={symbol}
                                        symbol={symbol}
                                        status={status}
                                        levelsDateIso={levelsDateIso}
                                        marketOpen={marketOpen}
                                        expanded={expandedSymbol === symbol}
                                        onToggle={() => setExpandedSymbol((current) => current === symbol ? null : symbol)}
                                    />
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={10} className="px-3 py-4 text-center text-muted">
                                        {orbEnabled ? "Waiting for market data..." : "Strategy offline"}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <Modal
                isOpen={showConfigModal}
                onClose={() => setShowConfigModal(false)}
                title="ORB Strategy Setup"
                width="max-w-4xl"
            >
                <StrategyConfigPanel strategyName="ORB" readOnly />
            </Modal>
        </>
    );
};

export default ORBStrategyContent;
